import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentPlusIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ClockIcon,
  UserPlusIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Badge, getStatusBadgeVariant, Modal, ModalFooter, ContentLoader, Input, Select, BackButton } from '@/components/common';
import {
  useLead,
  useLeadActivities,
  useDeleteLead,
  useConvertLead,
  useAddLeadActivity,
  useAssignLead,
  useUpdateLeadStatus,
  useAssignableUsers,
} from '@/hooks/useLeads';
import { useAuthStore } from '@/store';
import { useCan } from '@/hooks/useEmployeeRights';
// Item 7a. A converted lead IS a contact, and it wears the same tag as the
// contact record does -- read from the same one fact (`is_shared=false`), so
// the lead and the contact cannot disagree about whether a quote is
// outstanding. Imported by path for the same reason the Contact Details page
// does it: hooks/ and components/common/index.ts are outside this file domain.
import { ContactQuotesPendingTag } from '@/pages/quotes/quotesPending';
import type { LeadActivity } from '@/types';
import { RevealableIdentity } from '@/components/common/RevealableIdentity';
import { isMaskedIdentityField, revealLeadIdentity } from '@/services/identityReveal';
import { formatDate, formatDateTime, formatCurrency, titleCase } from '@/utils';

/**
 * #273: the `contact_details` keys, in display order, with the labels the
 * Contacts form uses. A key absent from a lead is simply not rendered.
 */
const CONTACT_DETAIL_LABELS: [string, string][] = [
  ['customer_type', 'Customer Type'],
  ['segment', 'Segment'],
  ['middle_name', 'Middle Name'],
  ['date_of_birth', 'Date of Birth'],
  ['gender', 'Gender'],
  ['marital_status', 'Marital Status'],
  ['occupation', 'Occupation'],
  ['annual_income', 'Annual Income'],
  ['company_type', 'Business Type'],
  ['incorporation_date', 'Date of Incorporation'],
  ['cin_number', 'CIN'],
  ['nature_of_business', 'Nature of Business'],
  ['annual_turnover', 'Annual Turnover'],
  ['num_employees', 'Number of Employees'],
  ['website', 'Website'],
  ['pan_number', 'PAN'],
  ['aadhaar_number', 'Aadhaar'],
  ['gst_number', 'GST'],
  ['passport_number', 'Passport'],
  ['driving_license', 'Driving Licence'],
  ['address_line1', 'Address Line 1'],
  ['address_line2', 'Address Line 2'],
  ['landmark', 'Landmark'],
  ['city_name', 'City'],
];

/** Mirrors `LeadActivity.ACTIVITY_TYPE_CHOICES` in
 *  backend/apps/leads/models.py.
 *
 *  This form used to send a hardcoded `activity_type: 'note'`, and there is no
 *  `note` in that vocabulary — the value is `note_added` — so every attempt to
 *  add an activity was rejected with "Please select a valid Activity Type".
 *  The user was never offered a choice to get wrong; the form simply sent an
 *  invalid one every time. */
// `won` is reached through Convert (it creates or links the contact) and `lost`
// needs a reason, so neither is a plain drop-down move.
const PIPELINE_STAGE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'nurturing', label: 'Nurturing' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'junk', label: 'Junk' },
];

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email_sent', label: 'Email Sent' },
  { value: 'email_received', label: 'Email Received' },
  { value: 'sms_sent', label: 'SMS Sent' },
  { value: 'whatsapp', label: 'WhatsApp Message' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'note_added', label: 'Note Added' },
  { value: 'document_shared', label: 'Document Shared' },
  { value: 'other', label: 'Other' },
];

/** Icon per activity type. Keyed on the REAL values: the old map tested for
 *  'email', 'meeting' and 'note', two of which are not activity types at all,
 *  so most rows rendered an empty circle. */
const ACTIVITY_ICON: Record<string, typeof PhoneIcon> = {
  call: PhoneIcon,
  email_sent: EnvelopeIcon,
  email_received: EnvelopeIcon,
  sms_sent: EnvelopeIcon,
  whatsapp: EnvelopeIcon,
  meeting: UserPlusIcon,
  note_added: PencilIcon,
  document_shared: PencilIcon,
  quote_sent: PencilIcon,
  proposal_sent: PencilIcon,
  follow_up: CalendarIcon,
  status_change: ArrowPathIcon,
  assignment: UserPlusIcon,
};

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityType, setActivityType] =
    useState<LeadActivity['activity_type']>('call');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [activitySubject, setActivitySubject] = useState('');
  const [activityDescription, setActivityDescription] = useState('');

  // Item 1: a POSP now reaches this page for a lead assigned to them.
  // Assigning and deleting stay broker-staff actions server-side
  // (LeadViewSet.POSP_ACTIONS), so the buttons are not offered here either --
  // a control that exists only to answer 403 is worse than no control.
  //
  // P1 (15 Sep): converting is now open to a POSP, on the same terms the API
  // enforces -- the lead is assigned to THEM and they hold `add_customer`.
  // Anything else would be a button whose only answer is 403.
  const currentRole = useAuthStore((s) => s.user?.role);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isPosp = currentRole === 'posp' || currentRole === 'super_posp';
  // B4: converting is leads Edit AND contacts Create (for every gated role);
  // assign and edit are leads Edit, delete is leads Delete.
  const canEditLeads = useCan('leads', 'edit');
  const canDeleteLeads = useCan('leads', 'delete');
  const canCreateContacts = useCan('contacts', 'create');
  const canCreatePolicy = useCan('policies', 'create');
  // B4: the post-convert "Create a quote" step opens /quotes/new (quotes Create).
  const canCreateQuote = useCan('quotes', 'create');
  const canAddCustomer = canEditLeads && canCreateContacts;

  const { data: lead, isLoading, error } = useLead(id!);
  // Three states, not two. A failed request used to render the same
  // "No activities recorded yet." as an empty timeline -- an authoritative
  // statement that there is nothing, made when we do not know.
  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useLeadActivities(id!);
  const deleteMutation = useDeleteLead();
  const convertMutation = useConvertLead();
  const assignMutation = useAssignLead();
  const statusMutation = useUpdateLeadStatus();
  // Only fetched while the modal is open — this is a broker-staff roster and
  // nothing on the page needs it until somebody chooses to assign.
  const { data: assignableUsers = [], isLoading: assignableLoading } = useAssignableUsers(!isPosp);
  // Set once a conversion succeeds; drives the "what next" dialog below.
  const [convertedCustomerId, setConvertedCustomerId] = useState<string | null>(null);
  // Conversion either CREATES a contact or LINKS an existing one; the endpoint
  // says which. The dialog claimed "Contact created" either way, directly under
  // a toast reading "Linked to existing customer".
  const [convertedCreated, setConvertedCreated] = useState(true);
  const addActivityMutation = useAddLeadActivity();

  const handleDelete = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => navigate('/leads'),
    });
  };

  const handleConvert = () => {
    // The endpoint answers with the customer it created or linked; going back
    // to the leads list threw that away and left the broker to hunt for the
    // customer they had just made.
    //
    // Item 4 removed "Create Quote" from this page, because a quote raised
    // against a lead had nowhere to attach -- a quote points at a Customer and
    // a lead is not one. Quoting therefore lives on the far side of
    // conversion, and conversion has to OFFER it: otherwise removing the
    // button just made quoting a lead slower, which is not what was asked for.
    convertMutation.mutate(id!, {
      onSuccess: (result) => {
        setConvertedCreated(Boolean(result.customer_created));
        setConvertedCustomerId(result.customer_id);
      },
    });
  };

  const handleAddActivity = () => {
    if (activitySubject) {
      addActivityMutation.mutate(
        {
          leadId: id!,
          data: {
            activity_type: activityType,
            subject: activitySubject,
            description: activityDescription,
          },
        },
        {
          onSuccess: () => {
            setShowActivityModal(false);
            setActivitySubject('');
            setActivityDescription('');
            setActivityType('call');
          },
        }
      );
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !lead) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Lead not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The lead you are looking for does not exist.</p>
        <Link to="/leads" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Leads
          </Button>
        </Link>
      </div>
    );
  }

  // CONTACTS-1. "Convert to Customer" never went away, because it hid on
  // `status !== 'converted'` and `converted` is not a Lead status — the
  // vocabulary is new/contacted/.../won/lost/junk, and converting sets `won`.
  // So the test was always true and the button stayed on a converted lead,
  // offering to convert it a second time.
  //
  // Gated on the FK the conversion actually writes, not on a status string a
  // broker can set by hand from the leads screen without any customer being
  // created. `converted_to_customer` is what LeadDetailSerializer sends; the
  // shared `Lead` type declares `converted_customer`, which the API has never
  // sent, hence the local widening (same approach as QuoteDetailPage).
  const conversion = lead as unknown as {
    converted_to_customer?: string | null;
    converted_to_customer_name?: string | null;
  };
  const isConverted = Boolean(conversion.converted_to_customer) || lead.status === 'won';
  // Item 10: the contact block the detail serializer sends inline. `null`
  // when this lead has not been converted -- never an empty object, so an
  // absent link is absent rather than nameless.
  const convertedCustomer = lead.converted_customer ?? null;
  // The contact id the "Quotes Pending" tag is keyed by. The inline block is
  // preferred because it is the one the serializer sends with a name attached;
  // `converted_to_customer` is the bare FK, and older payloads carry only that.
  const convertedContactId = convertedCustomer?.id ?? conversion.converted_to_customer ?? null;
  // #273: the Contacts-shaped fields captured with the lead. They are what
  // conversion copies onto the Customer, so they have to be visible on the
  // lead -- otherwise a broker cannot tell what a convert would carry over.
  const contactDetails = lead.contact_details ?? {};
  const contactDetailEntries = CONTACT_DETAIL_LABELS.filter(
    ([key]) => {
      const value = (contactDetails as Record<string, unknown>)[key];
      return value !== undefined && value !== null && value !== '';
    }
  );

  // Show the Vehicle card only when there is a vehicle to show -- a health or
  // life lead should not carry an empty motor block.
  const hasVehicleDetails = Boolean(
    lead.registration_number ||
      lead.vehicle_make ||
      lead.vehicle_model ||
      lead.vehicle_variant ||
      lead.vehicle_type ||
      lead.manufacturing_year,
  );

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/leads" />
      </div>
      <PageHeader
        title={lead.full_name}
        subtitle={lead.company_name || lead.lead_number}
        breadcrumbs={[
          { label: 'Leads', href: '/leads' },
          { label: lead.full_name },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {!isConverted && canAddCustomer && (!isPosp || lead.assigned_to === currentUserId) && (
              <Button
                variant="outline"
                leftIcon={<ArrowPathIcon className="w-5 h-5" />}
                onClick={handleConvert}
                isLoading={convertMutation.isPending}
              >
                Convert to Customer
              </Button>
            )}
            {/* Straight to Add Policy with this lead's customer and vehicle
                already filled in. Converting used to produce a Customer and
                stop there, so the broker re-typed the vehicle from the page
                they were looking at. */}
            {!isPosp && canEditLeads && (
              <Button
                variant="outline"
                leftIcon={<UserPlusIcon className="w-5 h-5" />}
                onClick={() => {
                  setAssignTo(lead.assigned_to ?? '');
                  setShowAssignModal(true);
                }}
              >
                {lead.assigned_to_name ? 'Reassign' : 'Assign'}
              </Button>
            )}
            {canCreatePolicy && (
              <Link to={`/policies/new?lead=${id}`}>
                <Button variant="outline" leftIcon={<DocumentPlusIcon className="w-5 h-5" />}>
                  Create Policy
                </Button>
              </Link>
            )}
            {canEditLeads && (
              <Link to={`/leads/${id}/edit`}>
                <Button variant="outline" leftIcon={<PencilIcon className="w-5 h-5" />}>
                  Edit
                </Button>
              </Link>
            )}
            {!isPosp && canDeleteLeads && (
            <Button
              variant="danger"
              leftIcon={<TrashIcon className="w-5 h-5" />}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
            )}
          </div>
        }
      />

      {/* Item 7a: the quotes this lead's contact is still waiting on. Keyed on
          the CONTACT the conversion created (`converted_to_customer`), because
          a quote belongs to a `customers.Customer`, never to a Lead -- so an
          unconverted lead has nothing to count and the tag draws nothing. */}
      <ContactQuotesPendingTag customerId={convertedContactId} className="mb-4" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader title="Contact Information" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <PhoneIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.phone}</p>
                </div>
              </div>
              {lead.alternate_phone && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <PhoneIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Alternate Phone</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.alternate_phone}</p>
                  </div>
                </div>
              )}
              {lead.email && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <EnvelopeIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.email}</p>
                  </div>
                </div>
              )}
              {lead.company_name && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Company</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {lead.company_name}
                      {lead.designation && ` - ${lead.designation}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Lead Details */}
          <Card>
            <CardHeader title="Lead Details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoItem label="Source" value={lead.source_name} />
              <InfoItem label="Campaign" value={lead.campaign_name || '-'} />
              {/* Item 3: the product the lead asked about -- including the
                  one a visitor picked on the public site, which is captured
                  in `product_interest` and was being read off a phantom
                  `insurance_type_name` that no serializer emits. */}
              <InfoItem
                label="Product Interest"
                value={lead.product_interest_display || lead.product_interest || '-'}
              />
              <InfoItem
                label="Estimated Premium"
                value={lead.estimated_premium ? formatCurrency(lead.estimated_premium) : '-'}
              />
              <InfoItem label="Assigned To" value={lead.assigned_to_name || 'Unassigned'} />
              <InfoItem
                label="Next Follow-up"
                value={lead.next_follow_up ? formatDateTime(lead.next_follow_up) : '-'}
              />
            </div>
            {lead.follow_up_notes && (
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Follow-up Remarks</p>
                <p className="text-sm text-gray-900 dark:text-white">{lead.follow_up_notes}</p>
              </div>
            )}
            {/* Item 10: the lead and the contact it became are one record, so
                the lead has to say which contact that is and take you there.
                `converted_to_customer` alone is a bare id nobody can read. */}
            {convertedCustomer && (
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Converted Contact</p>
                <Link
                  to={`/contacts/${convertedCustomer.id}`}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  {convertedCustomer.full_name} ({convertedCustomer.customer_code})
                </Link>
              </div>
            )}
            {lead.notes && (
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Notes</p>
                <p className="text-sm text-gray-900 dark:text-white">{lead.notes}</p>
              </div>
            )}
          </Card>

          {/* The motor block. The Lead model has carried vehicle_type/make/
              model/registration_number all along and the serializer exposed
              them, but no screen ever showed them -- so a motor lead looked
              like a name and a phone number, and whoever picked it up asked
              the customer for the vehicle a second time. */}
          {hasVehicleDetails && (
            <Card>
              <CardHeader
                title="Vehicle"
                subtitle="Carried onto the quote or policy created from this lead"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem label="Vehicle Number" value={lead.registration_number || '-'} />
                <InfoItem label="Vehicle Type" value={lead.vehicle_type || '-'} />
                <InfoItem label="Make" value={lead.vehicle_make || '-'} />
                <InfoItem label="Model" value={lead.vehicle_model || '-'} />
                <InfoItem label="Variant" value={lead.vehicle_variant || '-'} />
                <InfoItem label="Fuel Type" value={lead.vehicle_fuel_type || '-'} />
                <InfoItem
                  label="Manufacturing Year"
                  value={lead.manufacturing_year ? String(lead.manufacturing_year) : '-'}
                />
                <InfoItem
                  label="New Vehicle"
                  value={
                    lead.is_new_vehicle === null || lead.is_new_vehicle === undefined
                      ? '-'
                      : lead.is_new_vehicle
                        ? 'Yes'
                        : 'No'
                  }
                />
                <InfoItem
                  label="Existing Policy Expiry"
                  value={lead.existing_policy_expiry ? formatDate(lead.existing_policy_expiry) : '-'}
                />
              </div>
            </Card>
          )}

          {/* #273: captured Contacts fields */}
          {contactDetailEntries.length > 0 && (
            <Card>
              <CardHeader
                title="Captured Contact Details"
                subtitle="Copied onto the customer when this lead is converted"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactDetailEntries.map(([key, label]) =>
                  // #377: identity numbers arrive masked; Reveal fetches (and audits) the full value.
                  isMaskedIdentityField(key) ? (
                    <RevealableIdentity
                      key={key}
                      label={label}
                      maskedValue={String((contactDetails as Record<string, unknown>)[key])}
                      canReveal={Boolean(lead.can_reveal_identity)}
                      onReveal={() => revealLeadIdentity(lead.id, key)}
                    />
                  ) : (
                    <InfoItem
                      key={key}
                      label={label}
                      value={String((contactDetails as Record<string, unknown>)[key])}
                    />
                  )
                )}
              </div>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card>
            <CardHeader
              title="Activity Timeline"
              action={
                <Button size="sm" onClick={() => setShowActivityModal(true)}>
                  Add Activity
                </Button>
              }
            />
            {activitiesLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading the timeline…</p>
            ) : activitiesError ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                The activity timeline could not be loaded, so this lead's history is not
                shown. Reload the page to try again.
              </p>
            ) : activities && activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity, index) => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="relative">
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                        {(() => {
                          const Icon = ACTIVITY_ICON[activity.activity_type] ?? PencilIcon;
                          return <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />;
                        })()}
                      </div>
                      {index < activities.length - 1 && (
                        <div className="absolute top-8 left-4 w-0.5 h-full bg-gray-200 dark:bg-gray-700" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.subject}</p>
                        <Badge size="sm" variant="default">
                          {titleCase(activity.activity_type)}
                        </Badge>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{activity.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-gray-400">{activity.performed_by_name}</span>
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-400">
                            {formatDateTime(activity.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No activities recorded yet.</p>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Current Status</span>
                <Badge variant={getStatusBadgeVariant(lead.status)}>
                  {titleCase(lead.status)}
                </Badge>
              </div>
              {!isPosp && canEditLeads && !['won', 'lost'].includes(lead.status) && (
                <Select
                  label="Move to stage"
                  name="lead_stage"
                  options={PIPELINE_STAGE_OPTIONS}
                  value={lead.status}
                  disabled={statusMutation.isPending}
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== lead.status) {
                      statusMutation.mutate({ id: id!, status: e.target.value });
                    }
                  }}
                />
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Priority</span>
                <Badge variant={getPriorityBadgeVariant(lead.priority)}>
                  {titleCase(lead.priority)}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader title="Timeline" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CalendarIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(lead.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <ClockIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateTime(lead.updated_at)}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              <Button variant="outline" className="w-full justify-start" onClick={() => setShowActivityModal(true)}>
                Add Activity
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Delete Modal */}
      {/* Item 4: what happens next, now that Create Quote has gone from this
          page. A conversion that silently lands on the contact page leaves the
          broker to find their own way to a quote; naming both onward steps
          keeps quoting a lead one flow rather than two screens and a search. */}
      <Modal
        isOpen={convertedCustomerId !== null}
        onClose={() => navigate(`/contacts/${convertedCustomerId}`)}
        title={convertedCreated ? 'Contact created' : 'Linked to an existing contact'}
        description={
          convertedCreated
            ? 'This lead is now a contact. What would you like to do next?'
            : 'This lead matched a contact you already had, and has been linked to it. What would you like to do next?'
        }
      >
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => navigate(`/contacts/${convertedCustomerId}`)}
          >
            Open the contact
          </Button>
          {/* B15: this lead just converted, so it's the CONVERTED lead this
              contact should be quoted from — opens the wizard on Business
              Type with fields prefilled from it, same as the Contacts page's
              own "Create New Quote". */}
          {canCreateQuote && (
            <Button
              onClick={() =>
                navigate(`/quotes/new?customer=${convertedCustomerId}&lead=${id}&step=business_type`)
              }
            >
              Create a quote
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Lead"
        description="Are you sure you want to delete this lead? This action cannot be undone."
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={deleteMutation.isPending}
          >
            Delete Lead
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={`Assign ${lead.full_name}`}
      >
        <div className="space-y-4">
          {/* Three states, not two: an empty roster because the request failed
              is not the same as a broker with no staff, and offering an empty
              dropdown for either would be the same silent lie. */}
          {assignableLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading your team…</p>
          ) : assignableUsers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You have no employees or agents to assign this lead to yet.
            </p>
          ) : (
            <Select
              label="Assign to"
              name="assign_to"
              options={[
                { value: '', label: 'Select a person' },
                ...assignableUsers.map((candidate) => ({
                  value: candidate.id,
                  label: `${candidate.full_name} (${titleCase(candidate.role)})`,
                })),
              ]}
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
            />
          )}
          <Input
            label="Note (optional)"
            name="assign_notes"
            value={assignNotes}
            onChange={(e) => setAssignNotes(e.target.value)}
            placeholder="Why, or what to do next"
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button
            disabled={!assignTo}
            isLoading={assignMutation.isPending}
            onClick={() =>
              assignMutation.mutate(
                { id: id!, userId: assignTo, notes: assignNotes },
                {
                  onSuccess: () => {
                    setShowAssignModal(false);
                    setAssignNotes('');
                  },
                },
              )
            }
          >
            Assign
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Activity Modal */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Add Activity"
      >
        <div className="space-y-4">
          <Select
            label="Activity Type"
            name="activity_type"
            options={ACTIVITY_TYPE_OPTIONS}
            value={activityType}
            onChange={(e) =>
              setActivityType(e.target.value as LeadActivity['activity_type'])
            }
          />
          <Input
            label="Subject"
            name="activity_subject"
            value={activitySubject}
            onChange={(e) => setActivitySubject(e.target.value)}
            placeholder="Activity subject"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              rows={3}
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
              placeholder="Activity description..."
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowActivityModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddActivity}
            isLoading={addActivityMutation.isPending}
            disabled={!activitySubject}
          >
            Add Activity
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  );
}
